import { useState } from 'react';

import { cn } from '~/utils/cn';

import { useNearViewport } from '../hooks/use-near-viewport';

const PLACEHOLDER_EMBLEM = '/assets/images/lily-emblem-placeholder.svg';

/**
 * URLs this tab already loaded once. A remount (paging back, reopening a preview) paints them at
 * once — the bytes are in the browser cache — instead of waiting for the observer again.
 */
const loadedUrls = new Set<string>();

type Status = 'loading' | 'loaded' | 'error';

interface Props {
  /** Image URL; null/undefined renders the placeholder emblem in the same box. */
  src: string | null | undefined;
  alt: string;
  /** Intrinsic display size in CSS px — set on the `<img>` so the browser reserves the box. */
  width: number;
  height: number;
  /** Applied to the `<img>` (fit, padding); the wrapper always fills its parent box. */
  className?: string | undefined;
}

/**
 * catalog-enrichment's product image: nothing is requested until the box is within ~200px of the
 * viewport (shared IntersectionObserver), then a plain `<img>` with explicit width/height and
 * `decoding="async"` fades in over a muted box. Off-screen rows of a long list never load —
 * unlike the shared `CachedImage`, which preloads on mount.
 */
export function LazyImage({ src, alt, width, height, className }: Props) {
  const [ref, near] = useNearViewport();
  // Status keyed by src so a new URL restarts at "loading" without an effect.
  const [state, setState] = useState<{ src: string | null | undefined; status: Status }>(() => ({
    src,
    status: src && loadedUrls.has(src) ? 'loaded' : 'loading',
  }));
  if (state.src !== src) {
    setState({ src, status: src && loadedUrls.has(src) ? 'loaded' : 'loading' });
  }
  const status = src ? state.status : 'error';
  const show = !!src && (near || status === 'loaded');

  return (
    <span
      ref={ref}
      className={cn(
        'relative block size-full overflow-hidden',
        status === 'loading' && 'bg-muted animate-pulse',
        status === 'error' && 'bg-surface-progress-track flex items-center justify-center',
      )}
      aria-busy={status === 'loading' ? true : undefined}
    >
      {status === 'error' ? (
        <img src={PLACEHOLDER_EMBLEM} alt="" className="h-auto max-h-[40%] w-auto max-w-[40%]" />
      ) : show ? (
        <img
          src={src}
          alt={alt}
          width={width}
          height={height}
          decoding="async"
          loading="lazy"
          className={cn(
            'size-full object-cover transition-opacity duration-200',
            status === 'loaded' ? 'opacity-100' : 'opacity-0',
            className,
          )}
          onLoad={() => {
            loadedUrls.add(src);
            setState((prev) => (prev.src === src ? { src, status: 'loaded' } : prev));
          }}
          onError={() => {
            setState((prev) => (prev.src === src ? { src, status: 'error' } : prev));
          }}
        />
      ) : null}
    </span>
  );
}
